import { addMonths, isAfter, isBefore, startOfDay, addYears } from 'date-fns';
import { compact } from 'lodash-es';
import { getTitleValidator } from 'validators';
import { DiscountExpirationType } from '../LimitedTimeDiscountOfferModel';
import * as serviceBillingModel from '../service-billing/selectors';
import * as serviceCostModel from '../service-cost/selectors';
import { fromDateStampObject } from '../time';
import { ServiceStatusType } from './model';
import * as selectors from './selectors';
export const serviceValidationTextsPath = 'models.service.validation';
export function validateDayOfMonth(service, texts) {
    const billingDayOfMonth = selectors.billingDayOfMonth(service);
    const isBilledUpfront = selectors.isBilledUpfront(service);
    if (!billingDayOfMonth || isBilledUpfront == null) return [];
    return validateBillingDayOfMonth(billingDayOfMonth, isBilledUpfront) ? [
        texts.billedUpfrontLastDay()
    ] : [];
}
export function validateMaxCharges(service, texts) {
    const maxCharges = selectors.maxChargesLimit(service);
    const isMaxChargesEnabled = selectors.maxChargesEnabled(service);
    if (isMaxChargesEnabled) {
        if (maxCharges == null) {
            return [
                texts.maxChargesEmpty()
            ];
        } else if (maxCharges < 1 || maxCharges > 99) {
            return [
                texts.maxChargesInvalidRange()
            ];
        }
    }
    return [];
}
export function roundPricesErrorMsg(texts) {
    return texts.roundPricesError();
}
export function increaseMethodErrorMsg(texts) {
    return texts.increaseMethodError();
}
export function validatePriceIncrease(service, opts, texts) {
    const priceIncrease = selectors.priceIncrease(service);
    const increaseOn = priceIncrease.increaseOn();
    return compact([
        priceIncrease.exists() && isInvalidPercentIncrease(priceIncrease.percentIncrease()) && texts.amountMustBePositive(),
        isInvalidRoundPrices(service) && texts.roundPricesError(),
        priceIncrease.exists() && isInvalidIncreaseMethod(increaseOn, opts.relationshipEffectiveDate) && texts.increaseMethodError(),
        priceIncrease.exists() && increaseOn && opts.relationshipEffectiveDate && isBefore(increaseOn, opts.relationshipEffectiveDate) && texts.dateAfterEffective()
    ]);
}
export function isInvalidIncreaseMethod(increaseOn, relationshipEffectiveDate) {
    return !increaseOn && !!relationshipEffectiveDate;
}
export function isInvalidPercentIncrease(percentIncrease) {
    if (typeof percentIncrease === 'string') return !percentIncrease || +percentIncrease <= 0;
    return percentIncrease.isNegative() || percentIncrease.isZero();
}
export function isInvalidRoundPrices(service) {
    const nextYearPrice = selectors.nextYearPrice(service);
    return nextYearPrice != null && selectors.finalPrice(service, 1).gt(nextYearPrice);
}
export function validateName(name, options) {
    const { nameBlacklist, texts, label } = options;
    const trimmed = name.trim();
    if (!trimmed) return texts.fieldRequired({
        label
    });
    if (nameBlacklist && nameBlacklist.includes(trimmed)) return texts.serviceNameExists();
    const validation = getTitleValidator(label)(name);
    if (validation !== true) return typeof validation === 'string' ? validation : texts.fieldInvalid({
        label
    });
    return undefined;
}
/** Will return true if there's an error. */ export const validateBillingDayOfMonth = (billingDayOfMonth, isBilledUpfront)=>billingDayOfMonth.isLast() && isBilledUpfront;
export function validateIntegratedClass(service, texts) {
    const integratedClassId = selectors.integratedClassId(service);
    if (integratedClassId === '') return [
        texts.pleaseSelectClass()
    ];
    return [];
}
export function showMaxChargesIndicator(service, texts) {
    return selectors.maxChargesEnabled(service) && validateMaxCharges(service, texts).length === 0;
}
export function validateLimitedTimeDiscountOffer(service, now, texts) {
    const limitedTimeDiscountOffer = selectors.limitedTimeDiscountOffer(service);
    if (!limitedTimeDiscountOffer) return '';
    let error = '';
    const expirationType = selectors.limitedTimeDiscountOfferExpirationType(service);
    if (limitedTimeDiscountOffer.expiration.date) {
        const expirationDate = fromDateStampObject(limitedTimeDiscountOffer.expiration.date);
        if (isBefore(expirationDate, startOfDay(now))) error = texts.deadlineMustBeFuture();
    }
    if (expirationType === DiscountExpirationType.Days) {
        const days = selectors.limitedTimeDiscountOfferExpirationDays(service);
        if (days && days > 90) error = texts.discountOfferLessThan90Days();
        if (days !== undefined && days < 1) error = texts.discountOfferAtLeast1Day();
    } else if (expirationType === DiscountExpirationType.Date) {
        const expirationDate = selectors.limitedTimeDiscountOfferExpirationDate(service);
        if (expirationDate && isAfter(fromDateStampObject(expirationDate), addMonths(now, 4))) error = texts.discountOfferWithin4Months();
    }
    return error;
}
export function validateDiscountPeriodExpiration(service, now, texts, effectiveDate) {
    const cost = selectors.cost(service);
    const discountPeriodExpiration = cost.discount ? serviceCostModel.discountPeriodExpiration({
        discount: cost.discount
    }) : undefined;
    if (!discountPeriodExpiration) return '';
    let error = '';
    const expirationType = discountPeriodExpiration.type();
    if (expirationType === 'date') {
        const expirationDate = discountPeriodExpiration.date();
        if (expirationDate) {
            if (isBefore(expirationDate, startOfDay(now))) error = texts.discountExpirationMustBeFuture();
            if (isAfter(expirationDate, addYears(now, 4))) error = texts.discountExpirationUpTo4Years();
            if (effectiveDate && isBefore(expirationDate, effectiveDate)) error = texts.discountExpirationAfterEffective();
        }
    } else if (expirationType === 'period') {
        const periodType = discountPeriodExpiration.period();
        if (!periodType) return texts.invalidPeriodType();
        const amount = discountPeriodExpiration.amount();
        if (amount !== undefined && amount < 1) error = texts.discountExpirationAtLeast1();
        discountPeriodExpiration.matchPeriod(periodType, {
            days: ()=>{
                if (amount && amount > 90) error = texts.discountExpirationUpTo90Days();
            },
            weeks: ()=>{
                if (amount && amount > 12) error = texts.discountExpirationUpTo12Weeks();
            },
            months: ()=>{
                if (amount && amount > 36) error = texts.discountExpirationUpTo36Months();
            },
            years: ()=>{
                if (amount && amount > 4) error = texts.discountExpirationUpTo4Years();
            }
        });
    }
    return error;
}
export function validatePreapprovedAmount(service, texts) {
    var _selectors_changes;
    const cost = ((_selectors_changes = selectors.changes(service)) === null || _selectors_changes === void 0 ? void 0 : _selectors_changes.cost) || service.cost;
    const isControlledByAdvancedSettings = serviceCostModel.preapprovedControlledByAdvancedSettings(cost);
    if (!isControlledByAdvancedSettings) return '';
    const unitCap = serviceCostModel.unitCap(cost);
    if (!unitCap || unitCap.lessThanOrEqualTo(0)) return texts.preapprovedAmountGreaterThan0();
    return '';
}
export var BundleError = /*#__PURE__*/ function(BundleError) {
    BundleError["EmptyIncludedServices"] = "emptyIncludedServices";
    return BundleError;
}({});
export function validateBundles(service) {
    if (!service.bundle) return [];
    return validateBundleIncludedServices(service.bundle.services);
}
export function validateStartBillingAt(service, opts, texts) {
    const relationshipEffectiveDate = opts.relationshipEffectiveDate;
    const today = startOfDay(opts.now);
    const startBillingAt = selectors.startBillingAt(service);
    const oneYearAgo = addYears(today, -1);
    if (!startBillingAt || service.status.type === ServiceStatusType.Approved) {
        return [];
    }
    const startBillingAtToday = startOfDay(startBillingAt);
    if (!relationshipEffectiveDate) {
        return !isAfter(startBillingAtToday, today) ? [
            texts.startDateMustBeFuture()
        ] : [];
    }
    const relationshipEffectiveDateToday = startOfDay(relationshipEffectiveDate);
    if (isBefore(startBillingAtToday, relationshipEffectiveDateToday)) {
        return [
            texts.startDateAfterEffective()
        ];
    }
    if (isBefore(startBillingAtToday, oneYearAgo)) {
        return [
            texts.dateWithinPastYear()
        ];
    }
    return [];
}
export function validateBundleIncludedServices(includedServices) {
    return includedServices.length === 0 ? [
        "emptyIncludedServices"
    ] : [];
}
export var OneTimeInStagesError = /*#__PURE__*/ function(OneTimeInStagesError) {
    OneTimeInStagesError["InvalidStages"] = "billInStagesInvalidStages";
    OneTimeInStagesError["NonPercentageDiscount"] = "billInStagesNonPercentageDiscount";
    return OneTimeInStagesError;
}({});
export function validateOneTimeInStages(service, scheduleContext) {
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    if (!oneTimeInStages) return [];
    const errors = [];
    const discount = selectors.discount(service);
    if (discount && discount.isValue()) errors.push("billInStagesNonPercentageDiscount");
    if (oneTimeInStages.hasInvalidStages({
        cost: service.cost,
        scheduleContext
    })) {
        errors.push("billInStagesInvalidStages");
    }
    return errors;
}
